import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import Overline from "../components/Overline";
import { paths } from "../routing/paths";

/*
 * /chai-club — monthly giving in the redesign. Same PayPal hosted
 * subscription button as before (hosted_button_id + os0 option), with the
 * five monthly amounts as selectable chips instead of a <select>. The
 * homepage Support band can deep-link a preset via ?amount=.
 */

const PAYPAL_BUTTON_ID = "NJEJTL53VWCWU";

// PayPal option values are fixed by the hosted button; label = monthly USD.
const TIERS = [
  { option: "Option 1", amount: 5 },
  { option: "Option 2", amount: 10 },
  { option: "Option 3", amount: 18 },
  { option: "Option 4", amount: 36 },
  { option: "Option 5", amount: 72 },
];

export default function ChaiClubPage() {
  const [params] = useSearchParams();
  const requested = Number(params.get("amount"));
  const [tier, setTier] = useState(
    () => TIERS.find((t) => t.amount === requested) || TIERS.find((t) => t.amount === 18)
  );

  return (
    <PublicLayout>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <Link to={paths.donations()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Donate
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Chai Club</span>
        </nav>

        <section className="flex flex-col gap-2">
          <Overline>Support</Overline>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            Join our Chai Club!
          </h1>
          <p className="m-0 text-[18px] font-normal leading-7 text-ic-body max-w-[720px]">
            Partner with the learning every month. A recurring gift helps us record more classes and
            expand our reach.
          </p>
        </section>

        <section className="grid gap-5 items-start grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          <form
            action="https://www.paypal.com/cgi-bin/webscr"
            method="post"
            target="_top"
            className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark p-6 flex flex-col gap-5"
          >
            <input type="hidden" name="cmd" value="_s-xclick" />
            <input type="hidden" name="hosted_button_id" value={PAYPAL_BUTTON_ID} />
            <input type="hidden" name="currency_code" value="USD" />
            <input type="hidden" name="os0" value={tier.option} />

            <h2 className="m-0 font-bold text-white tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
              Give a monthly donation
            </h2>

            <div className="flex flex-col gap-2">
              <span id="chai-amount-label" className="text-[14px] leading-5 font-medium text-ic-lightGray">
                Monthly amount (USD)
              </span>
              <div role="radiogroup" aria-labelledby="chai-amount-label" className="flex gap-2 flex-wrap">
                {TIERS.map((t) => {
                  const active = t.option === tier.option;
                  return (
                    <button
                      key={t.option}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setTier(t)}
                      className={
                        active
                          ? "min-w-[72px] h-12 px-4 bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                          : "min-w-[72px] h-12 px-4 bg-transparent text-ic-offwhite font-sans text-[18px] font-semibold border border-white/30 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                      }
                    >
                      ${t.amount}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="submit"
                name="submit"
                className="bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus"
              >
                Subscribe · ${tier.amount}/month
              </button>
              <span className="text-[14px] leading-5 text-ic-lightGray">Checkout continues securely on PayPal.</span>
            </div>
          </form>

          <aside className="flex flex-col gap-4">
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-2">
              <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-slate">Give by card instead</span>
              <p className="m-0 text-[16px] leading-6 text-ic-body">
                The Donate page takes card gifts too — choose <strong>Monthly</strong> there to join the Chai Club.
              </p>
              <Link to={paths.donations()} className="text-ic-blue text-[16px] font-semibold underline inline-flex items-center min-h-[44px]">
                Go to Donate →
              </Link>
            </div>
            <div className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised p-6 flex flex-col gap-2">
              <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-slate">Prefer to arrange it by email?</span>
              <p className="m-0 text-[16px] leading-6 text-ic-body">Email us and we'll set up your membership together.</p>
              <a href="mailto:info@insidechassidus.org" className="text-ic-blue text-[16px] font-semibold underline inline-flex items-center min-h-[44px]">
                Email us →
              </a>
            </div>
          </aside>
        </section>
      </main>
    </PublicLayout>
  );
}
